import { Component, OnDestroy, OnInit } from '@angular/core';
import { ReportingService } from '../services/reporting/reporting.service';
import { DayReport } from '../models/dayreport';
import { FormControl } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { EmployeeService } from '../services/employee/employee.service';
import { EmployeeListItem } from '../models/employeelistitem';
import { PrepareListData } from '../helpers/preplistdata';
import { ReportSummaries } from '../models/reportsummaries';
import { ReturnObject } from '../models/returnObject';
import { InternalMsgService } from '../services/internal.msg.service';
import { AccountService } from '../services/account.service';
import { AccountItem } from '../models/accountitem';
import { Sort } from '../models/sort';
import { StatusMessagesService } from '../services/statusmessage.service';

@Component({
    selector: 'detailed-reporting',
    templateUrl: './detailedreporting.component.html',
    styleUrls: ['./detailedreporting.component.css'],
    providers: [],
})

export class DetailedReportingComponent implements OnInit, OnDestroy {
    sortField: string = "weekNo";
    loggedAccount: AccountItem = new AccountItem;
    selectedEmployee: string = '';
    dataSource: DayReport[] = [];
    summary: ReportSummaries = new ReportSummaries();
    fromDate: string = '';
    toDate: string = '';
    spinnerMessage: string = 'Please wait, report data is fetched';
    employeeList: EmployeeListItem[] = [];
    reportTotalHours: number = 0;
    rowCount: number = 0;
    msgSubscr: Subscription = new Subscription;

    constructor(private reportingService: ReportingService,
        private msgService: InternalMsgService,
        private statusMessagesService: StatusMessagesService,
        private accountService: AccountService,
        private employeeService: EmployeeService) {
    }

    ngOnInit() {
        const today = new Date();
        this.fromDate = today.toISOString().toString().split('T')[0];
        this.toDate = today.toISOString().split('T')[0];
        const p = this.loggedAccount;
        
        window.addEventListener("tdsSort", (event: any) => {
            const details = event['detail'];
            const sortObj: Sort = {
                active: details['columnKey'],
                direction: details['sortingDirection']
            }
            this.sort(sortObj);
        });

        this.msgSubscr = this.msgService.loggedInAccountSet$.subscribe(
            account => {
                this.loggedAccount = account;
                if (this.loggedAccount.reporting) {
                    this.getEmployeeList();
                }
                this.setDefaultEmp();
            });
        this.accountService.fetchAccount();
    }

    ngOnDestroy() {
        this.msgSubscr.unsubscribe();
    }


    // ----------------------------------------------------------------------------------
    // -- HTTP --------------------------------------------------------------------------
    // ----------------------------------------------------------------------------------
    getReportData() : void {
        this.msgService.ontoggleSpinnerSet(true);

        this.reportingService.getDetailedReportData(this.getEmailQueryString(), this.fromDate, this.toDate)
            .subscribe( { 
                next: (data: ReturnObject<DayReport[]>) => {
                    if (data.isFailure) {
                        this.statusMessagesService.showToasterError('Error', data.failureMessage);
                        return;
                    }
                    PrepareListData.prepareData(data.value, this.summary, this.sortField).then(data => {
                        this.dataSource = data;
                        if (!data) {
                            this.dataSource = [];
                        }
                        this.countHours();
                        this.countRows();
    
                    });
                },
                error: (err) => {         
                    this.statusMessagesService.showHttpError(err);
                },
                complete: () => {         
                    this.msgService.ontoggleSpinnerSet(false);
                }
            }
        );
    }
    
    getEmployeeList() : void {
        this.msgService.ontoggleSpinnerSet(true);

        this.employeeService.getEmployeeList()
            .subscribe( { 
                next: (data: ReturnObject<EmployeeListItem[]>) => {
                    if (data.isFailure) {
                        this.statusMessagesService.showToasterError('Error', data.failureMessage);
                        return;
                    }
                    this.employeeList = data.value;

                },
                error: (err) => {         
                    this.statusMessagesService.showHttpError(err);
                },
                complete: () => {         
                    this.msgService.ontoggleSpinnerSet(false);
                }
            }
        );
    }
    

    countHours() {
        this.reportTotalHours = 0;
        if (this.dataSource && this.dataSource.length > 0) {
            for (let days of this.dataSource) {
                if (days.isSubTotalRow) {
                    this.reportTotalHours += days.hours;
                }
            }
        }
    }

    countRows() {
        this.rowCount = 0;
        if (this.dataSource && this.dataSource.length > 0) {
            for (let days of this.dataSource) {
                if (!days.isSubTotalRow) {
                    this.rowCount += 1;
                }
            }
        }
    }

    setDefaultEmp() {
       this.selectedEmployee = `${this.loggedAccount.empName} - ${this.loggedAccount.email}`;
    }

    filterEmployees(searchText: string): EmployeeListItem[] {
        return this.employeeList.filter(employee => {
            if (typeof searchText !== 'string') {
                return false;
            }
            const input = searchText.toLowerCase();
            return employee.empName !== null
                ? employee.email.toLowerCase().indexOf(input) >= 0 || employee.empName.toLowerCase().indexOf(input) >= 0
                : false;
        });
    }

    getEmailQueryString(): string {
        try {
            if (!this.loggedAccount.reporting && !this.loggedAccount.sysAdmin) {
                this.setDefaultEmp();
            }

            return this.loggedAccount.reporting
                ? this.selectedEmployee.trim().split(' - ')[1]
                : this.loggedAccount.email;
        } catch (error) {
            return '';
        }
    }

    async sort(sort: Sort) {
        if (this.dataSource.length === 0) return;
        PrepareListData.sort(this.dataSource, sort, this.sortField ).then( data => {
            this.dataSource = data; 
        });
    }

    selectEmployee(employee: EmployeeListItem) {
        this.selectedEmployee = `${employee.empName} - ${employee.email}`;
    }

    clearText() {
        this.selectedEmployee = "";
    }

    getDisplayWeek(weekNo: string): string {
        return PrepareListData.getDisplayWeek(weekNo);
    }

    isSubmitDisabled(): boolean {
        if (this.fromDate == '' || this.toDate == '') return true;

        const dtFrom: Date = new Date(this.fromDate);
        const dtTo: Date = new Date(this.toDate);
        return this.selectedEmployee.trim() === ''
        || (dtTo < dtFrom);
    }
}
